<template>
  <div>
    <v-layout row justify-center>
      <v-dialog
        v-model="show"
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
      >
        <v-card style="background-color: #f5f5f5">
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="onClickCloseModal()">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ toolbarTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn
                class="pa-3"
                style="border-radius: 25px; height: auto;"
                color="secondary"
                :disabled="importing"
                @click="onClickImportInvoices()"
              >
                <v-progress-circular
                  v-if="importing"
                  color="secondary"
                  indeterminate
                ></v-progress-circular>
                <template v-else>
                  <v-icon class="black--text mr-2">
                    mdi-check
                  </v-icon>
                  <span class="black--text">IMPORTAR</span>
                </template>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-layout style="background-color: #f5f5f5" align-center justify-center row fill-height>
            <v-flex xs12 sm12 md8 lg6>
              <v-form ref="importInvoicesForm">
                <v-layout align-center justify-center column fill-height>
                  <v-flex xs12 class="width-100 mt-3">
                    <file-pond
                      class="mt-5"
                      name="import_file_xlsx"
                      ref="pond"
                      label-idle="Sube tu fichero excel aquí"
                      :storeAsFile="true"
                      accepted-file-types="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      labelInvalidField="El fichero seleccionado no es compatible"
                      credits=""
                      v-on:removefile="removeFile()"
                      v-on:addfile="addFile"
                    />
                  </v-flex>
                  <v-flex xs12 class="width-100 mt-3">
                    <h4>
                      <strong>
                        Descarga
                        <a href="/files/plantilla_importar_facturas.xlsx" download>aquí</a>
                        la plantilla que debes rellenar para importar facturas correctamente
                      </strong>
                    </h4>
                  </v-flex>
                  <v-flex v-if="invoicesToImport.length" xs12 class="width-100 mt-3">
                    <v-alert :value="true" color="info" icon="info" outline>
                      {{ `Se van a importar ${invoicesToImport.length} facturas` }}
                    </v-alert>
                  </v-flex>
                  <v-flex
                    v-if="invoicesToImport.length && !totalErrors"
                    xs12
                    class="width-100 mt-3"
                  >
                    <v-alert :value="true" color="success" icon="check_circle" outline>
                      {{ `El fichero no contiene ningún error` }}
                    </v-alert>
                  </v-flex>
                  <v-flex v-if="invoicesToImport.length && totalErrors" xs12 class="width-100 mt-3">
                    <v-alert :value="true" color="warning" icon="warning" outline>
                      {{ `Hay ${totalErrors} errores en el fichero` }}
                    </v-alert>
                  </v-flex>
                </v-layout>
              </v-form>
            </v-flex>
          </v-layout>
          <v-layout row justify-center>
            <v-flex xs12 class="pa-5 mt-3">
              <v-data-table
                :headers="sheetHeaders"
                :items="sheetData"
                :pagination.sync="invoicesPagination"
              >
                <template v-slot:no-data>
                  <v-alert :value="true" color="info" icon="info" outline>
                    {{ $t('No hay información que mostrar con los filtros seleccionados :(') }}
                  </v-alert>
                </template>
                <template v-slot:headers="props">
                  <tr>
                    <th
                      v-for="(header, index) in props.headers"
                      :key="`th_${index}`"
                      :class="['text-xs-center']"
                    >
                      {{ header }}
                    </th>
                  </tr>
                </template>
                <template v-slot:items="props">
                  <tr>
                    <td
                      v-for="(header, index) in sheetHeaders"
                      :bgcolor="validColorField(index, props.item[index])"
                      :key="`td-${index}`"
                      :class="['text-xs-center']"
                    >
                      {{ props.item[index] }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
        </v-card>
      </v-dialog>
      <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
        {{ snackText }}
      </v-snackbar>
    </v-layout>
  </div>
</template>

<script>
import apiCtrl from '@/services/api/abstract/abstract-api';
// Import FilePond
import vueFilePond from 'vue-filepond';
// Import plugins
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type/dist/filepond-plugin-file-validate-type.esm.js';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.esm.js';
// Import styles
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import XLSX from 'xlsx';
// Create FilePond component
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

// Import moment
const moment = require('moment');
require('moment/locale/es');

export default {
  name: 'OpenBillingImportInvoiceModal',
  components: { FilePond },
  props: {
    showModal: { type: Boolean, default: false },
    invoiceType: { type: String, default: 'factura' },
  },
  data() {
    return {
      snack: false,
      snackText: '',
      snackColor: 'info',
      sheetHeaders: [
        'NÚMERO',
        'FECHA',
        'FECHA DE VENCIMIENTO',
        'ESTADO',
        'CATEGORÍA',
        'TIPO DE OPERACIÓN',
        'CIF/NIF',
        'CLIENTE',
        'DIRECCIÓN DEL CLIENTE',
        'CÓDIGO POSTAL',
        'CIUDAD',
        'PROVINCIA',
        'PAÍS',
        'MÉTODO DE COBRO',
        'DESCRIPCIÓN',
        'CANTIDAD',
        'BASE IMPONIBLE',
        'DESCUENTO',
        'IMPORTE DESCUENTO',
        'TOTAL BASE IMPONIBLE',
        '% IVA',
        'IMPORTE IVA',
        '% IRPF',
        'IMPORTE IRPF',
        'TOTAL',
      ],
      sheetData: [],
      invoicesToImport: [],
      totalErrors: 0,
      importing: false,
      invoicesPagination: {
        descending: true,
        page: 1,
        rowsPerPage: 25, // -1 for All",
        sortBy: '',
        totalItems: 0,
      },
    };
  },
  mounted() {},
  computed: {
    show() {
      return this.showModal;
    },
    toolbarTitle() {
      return 'IMPORTAR FACTURAS';
    },
    billingProfileId() {
      return this.$store.getters.currentOpenbillingProfileId;
    },
    clavedat() {
      return this.$store.state.clavedat;
    },
  },
  methods: {
    showSnackMessage(action, message) {
      this.snackColor = action;
      this.snackText = message;
      this.snack = true;
    },
    onClickCloseModal() {
      this.$emit('click-on-close');
    },
    onClickImportInvoices() {
      if (!this.totalErrors && this.invoicesToImport.length) {
        this.importing = true;
        const data = {
          invoices: [...this.invoicesToImport],
          openbilling_profile_id: this.billingProfileId,
          clavedat: this.clavedat,
        };
        apiCtrl
          .post(`/openbilling/${this.clavedat}/invoices/import-excel`, data)
          .then(res => {
            if (res) {
              this.$emit('on-saved-invoices', res);
            }
            this.importing = false;
          })
          .catch(err => (this.importing = false));
      } else if (!this.invoicesToImport.length) {
        this.showSnackMessage('error', 'No hay facturas para importar');
      } else {
        this.showSnackMessage('error', 'Hay errores en el fichero');
      }
    },
    async addFile(error, file) {
      if (file) {
        let reader = new FileReader();
        // let name = file.file.name;

        reader.onload = e => {
          let data = e.target.result;
          let workbook = XLSX.read(data, { type: 'binary', cellDates: true });

          /* DO SOMETHING WITH workbook HERE */
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const sheetToJson = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          if (sheetToJson.length > 0) {
            // Remove first element (headers)
            sheetToJson.shift();
            this.sheetData = JSON.parse(JSON.stringify(sheetToJson));
            // Convert dates because excel return as JS Date
            this.sheetData.map(row => {
              row[1] = row[1] ? moment(row[1]).format('DD/MM/YYYY') : row[1];
              row[2] = row[2] ? moment(row[2]).format('DD/MM/YYYY') : row[2];
            });
            this.manageSheetData(sheetToJson);
          }
        };
        try {
          reader.readAsBinaryString(file.file);
        } catch (e) {
          this.showSnackMessage(
            'error',
            'Se ha producido un error al importar los datos. Vuelva a intentarlo por favor.',
          );
        }
      }
    },
    manageSheetData(sheetData) {
      const numbers = [];
      const invoices = [];
      this.totalErrors = 0;
      sheetData.forEach(row => {
        const number = row[0];
        this.countTotalErrors(row);
        // // Convert dates because excel return as JS Date
        row[1] = row[1] ? moment(row[1]).format('YYYY-MM-DD') : row[1];
        row[2] = row[2] ? moment(row[2]).format('YYYY-MM-DD') : row[2];
        if (!numbers.includes(number)) {
          const invoice = {
            clavedat: this.clavedat,
            openbilling_profile_id: this.billingProfileId,
            code: row[0],
            date: row[1],
            expiration_date: row[2],
            status: row[3],
            invoice_category: row[4],
            operation_type: row[5],
            contact_cif: row[6],
            contact_name: row[7],
            contact_address: row[8],
            contact_postal_code: row[9],
            contact_city: row[10],
            contact_province: row[11],
            contact_country: row[12],
            payment_method: row[13],
            order_items: [
              {
                clavedat: this.clavedat,
                description: row[14],
                quantity: row[15],
                tax_base: row[16],
                discount_type: row[17],
                discount_amount: row[18],
                total_amount: row[19],
                tax: row[20],
                tax_amount: row[21],
                irpf: row[22],
                irpf_amount: row[23],
                total_amount_with_taxes: row[24],
              },
            ],
          };
          invoices.push(invoice);
          numbers.push(number);
        } else {
          const invoice = invoices.find(invoice => invoice.code === number);
          invoice.order_items.push({
            clavedat: this.clavedat,
            description: row[14],
            quantity: row[15],
            tax_base: row[16],
            discount_type: row[17],
            discount_amount: row[18],
            total_amount: row[19],
            tax: row[20],
            tax_amount: row[21],
            irpf: row[22],
            irpf_amount: row[23],
            total_amount_with_taxes: row[24],
          });
        }
      });
      this.invoicesToImport = invoices;
    },
    validColorField(index, value) {
      let color = '#FFFFFF';
      // code
      if (index === 0 && !!value !== true) {
        color = '#FFB94F';
      }
      // date
      if (index === 1) {
        if (!this.validFormatDate(value)) {
          color = '#FFB94F';
        }
      }
      // expiration_date
      if (index === 2) {
        if (!!value && !this.validFormatDate(value)) {
          color = '#FFB94F';
        }
      }
      // status
      if (index === 3 && !this.validStatus(value)) {
        color = '#FFB94F';
      }
      // invoice_category
      if (index === 4 && !this.validCategory(value)) {
        color = '#FFB94F';
      }
      // operation_type
      if (index === 5 && !this.validOperationType(value)) {
        color = '#FFB94F';
      }
      // contact_cif
      if (index === 6 && !value) {
        color = '#FFB94F';
      }
      // contact_name
      if (index === 7 && !value) {
        color = '#FFB94F';
      }
      // contact_address
      if (index === 8 && !value) {
        color = '#FFB94F';
      }
      // contact_postal_code
      if (index === 9 && !value) {
        color = '#FFB94F';
      }
      // contact_city
      if (index === 10 && !value) {
        color = '#FFB94F';
      }
      // contact_province
      if (index === 11 && !value) {
        color = '#FFB94F';
      }
      // contact_country
      if (index === 12 && !value) {
        color = '#FFB94F';
      }
      // payment_method
      if (index === 13 && !this.validPaymentMethodType(value)) {
        color = '#FFB94F';
      }
      // description
      if (index === 14 && !value) {
        color = '#FFB94F';
      }
      // quantity
      if (index === 15 && !this.validNumber(value, false)) {
        color = '#FFB94F';
      }
      // tax_base
      if (index === 16 && !this.validNumber(value, false)) {
        color = '#FFB94F';
      }
      // discount_type
      if (index === 17 && !this.validDiscountType(value)) {
        color = '#FFB94F';
      }
      // discount_amount
      if (index === 18 && !this.validNumber(value)) {
        color = '#FFB94F';
      }
      // total_amount
      if (index === 19 && !this.validNumber(value, false)) {
        color = '#FFB94F';
      }
      // tax
      if (index === 20 && !this.validNumber(value)) {
        color = '#FFB94F';
      }
      // tax_amount
      if (index === 21 && !this.validNumber(value)) {
        color = '#FFB94F';
      }
      // irpf
      if (index === 22 && !this.validNumber(value)) {
        color = '#FFB94F';
      }
      // irpf_amount
      if (index === 23 && !this.validNumber(value)) {
        color = '#FFB94F';
      }
      // total_amount_with_taxes
      if (index === 24 && !this.validNumber(value, false)) {
        color = '#FFB94F';
      }

      return color;
    },
    validFormatDate(value) {
      if (!value) {
        return false;
      }
      return (
        moment(value, 'DD/MM/YYYY', true).isValid() ||
        moment(value, 'MM/DD/YYYY', true).isValid() ||
        moment(value, 'MM-DD-YYYY', true).isValid()
      );
    },
    validStatus(value) {
      return ['Impagada', 'Pendiente', 'Cobrada'].includes(value);
    },
    validCategory(value) {
      return [
        'Ventas de mercaderías',
        'Devoluciones de mercaderías',
        'Prestaciones de servicios',
        'Ingresos por arrendamientos',
        'Ingresos por créditos',
      ].includes(value);
    },
    validOperationType(value) {
      return [
        'Operación interior',
        'Entregas intracomunitarias',
        'Exportaciones',
        'Operaciones no sujetas',
        'Operación exenta de IVA',
      ].includes(value);
    },
    validPaymentMethodType(value) {
      if (value) {
        return [
          'Efectivo',
          'Transferencia',
          'Tarjeta bancaria',
          'Domiciliación bancaria',
          'Paypal',
          'Bizum',
        ].includes(value);
      }
      return true;
    },
    validDiscountType(value) {
      if (value) {
        return ['%', '€'].includes(value);
      }
      return true;
    },
    validNumber(value, emptyIsValid = true) {
      if (value) {
        return !isNaN(parseFloat(value));
      }

      return emptyIsValid;
    },
    countTotalErrors(row) {
      for (let index = 0; index < row.length; index++) {
        // code
        if (index === 0 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // date
        if (index === 1) {
          if (!this.validFormatDate(row[index])) {
            this.totalErrors = this.totalErrors + 1;
          }
        }
        // expiration_date
        if (index === 2) {
          if (!!row[index] && !this.validFormatDate(row[index])) {
            this.totalErrors = this.totalErrors + 1;
          }
        }
        // status
        if (index === 3 && !this.validStatus(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // invoice_category
        if (index === 4 && !this.validCategory(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // operation_type
        if (index === 5 && !this.validOperationType(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_cif
        if (index === 6 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_name
        if (index === 7 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_address
        if (index === 8 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_postal_code
        if (index === 9 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_city
        if (index === 10 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_province
        if (index === 11 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // contact_country
        if (index === 12 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // payment_method
        if (index === 13 && !this.validPaymentMethodType(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // description
        if (index === 14 && !row[index]) {
          this.totalErrors = this.totalErrors + 1;
        }
        // quantity
        if (index === 15 && !this.validNumber(row[index], false)) {
          this.totalErrors = this.totalErrors + 1;
        }
        // tax_base
        if (index === 16 && !this.validNumber(row[index], false)) {
          this.totalErrors = this.totalErrors + 1;
        }
        // discount_type
        if (index === 17 && !this.validDiscountType(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // discount_amount
        if (index === 18 && !this.validNumber(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // total_amount
        if (index === 19 && !this.validNumber(row[index], false)) {
          this.totalErrors = this.totalErrors + 1;
        }
        // tax
        if (index === 20 && !this.validNumber(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // tax_amount
        if (index === 21 && !this.validNumber(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // irpf
        if (index === 22 && !this.validNumber(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // irpf_amount
        if (index === 23 && !this.validNumber(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
        // total_amount_with_taxes
        if (index === 24 && !this.validNumber(row[index])) {
          this.totalErrors = this.totalErrors + 1;
        }
      }
    },
    removeFile() {
      this.invoicesToImport = [];
      this.totalErrors = 0;
      this.sheetData = [];
    },
  },
  watch: {},
};
</script>

<style scoped>
.width-100 {
  width: 100%;
}
.warning-field {
  background-color: red;
}
</style>
